import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { QuickActions } from '@/components/dashboard/quick-actions'
import { QuickstartCard } from '@/components/dashboard/quickstart-card'
import { RecentActivityCard } from '@/components/dashboard/recent-activity-card'
import {
  CredentialsCard,
  DataRemainingCard,
  PlanCard,
  StatRowSkeleton,
  UsedThisPeriodCard,
} from '@/components/dashboard/stat-cards'
import { PageHeader } from '@/components/PageHeader'
import { SubscribeGate } from '@/components/SubscribeGate'
import { UsageChartCard } from '@/components/UsageChart'
import { usageKeys } from '@/components/usage/keys'
import { UsageByCredentialCard } from '@/components/usage/usage-by-credential'
import { orgQueries } from '@/lib/org/queries'
import { getOrgUsage } from '@/lib/usage/server'

export const Route = createFileRoute('/_authenticated/dashboard')({
  // Prefetch in the loader so hovering a sidebar link warms these queries before
  // the click (defaultPreload: 'intent'). Only the plan status is awaited — it
  // decides between the subscribe gate and the real page, so rendering without
  // it would flash the wrong layout. The rest stream in behind the skeletons
  // they already had, which keeps navigation from blocking on four round trips.
  loader: async ({ context: { queryClient, activeOrg } }) => {
    // Stamped ONCE here, not read in render. SSR and hydration then compute the
    // "resets in N days" figure from the same instant instead of two instants a
    // few hundred ms apart, which intermittently disagreed across a day
    // boundary — see DataRemainingCard's `now` prop. Returned on both paths so
    // useLoaderData is never undefined.
    const now = Date.now()
    if (!activeOrg) return { now }
    const orgId = activeOrg.id
    // Swallow the failure: the component reads this same query with useQuery and
    // already handles the error branch (it falls through to the subscribe gate),
    // which is exactly what happened before this loader existed. Letting it
    // throw here would escalate a single failed card into the route's error
    // boundary and blank the whole page.
    await queryClient
      .ensureQueryData(orgQueries.planStatus(orgId))
      .catch(() => {})
    // Kick off the non-gating queries. On the CLIENT they stay un-awaited so a
    // tab switch isn't blocked on them — the tiles have skeletons.
    //
    // On the SERVER they must be awaited. Un-awaited, the SSR render happens
    // before they resolve (tiles render empty) but they frequently resolve
    // before the query cache is dehydrated into the HTML — so the client's first
    // render has data the server's didn't, which is a hydration mismatch. That
    // race is why it was intermittent. Awaiting here also means the first paint
    // is complete instead of a flash of skeletons.
    const rest = [
      queryClient.prefetchQuery(orgQueries.balance(orgId)),
      queryClient.prefetchQuery(orgQueries.credentials(orgId)),
      queryClient.prefetchQuery(orgQueries.usage(orgId, '30d')),
    ]
    if (import.meta.env.SSR) await Promise.all(rest)
    return { now }
  },
  component: Dashboard,
})

function Dashboard() {
  // The active org comes from the route context (resolved once in
  // `_authenticated`'s beforeLoad), not from a client-side org request — that
  // request used to gate every query below it, serialising a whole round trip
  // ahead of the first fetch.
  const { activeOrg } = Route.useRouteContext()
  const { now } = Route.useLoaderData()
  const orgId = activeOrg?.id ?? null

  const planQuery = useQuery(orgQueries.planStatus(orgId))
  const balanceQuery = useQuery(orgQueries.balance(orgId))
  const credentialsQuery = useQuery({
    ...orgQueries.credentials(orgId),
    enabled: !!orgId,
  })
  // The 30-day series drives the burn-rate projection on the "Data remaining"
  // tile; it shares its key with the usage chart below so the two dedupe into
  // one fetch.
  const usageQuery = useQuery(orgQueries.usage(orgId, '30d'))
  // Only the fresh-org quickstart branch reads this, so skip the request for
  // non-entitled orgs and orgs that already have credentials.
  const connQuery = useQuery({
    ...orgQueries.proxyConnectionInfo(),
    enabled:
      (planQuery.data?.entitled ?? false) &&
      !credentialsQuery.isPending &&
      (credentialsQuery.data?.length ?? 0) === 0,
  })

  const plan = planQuery.data
  const entitled = plan?.entitled ?? false
  const credentials = credentialsQuery.data ?? []
  const usage = usageQuery.data ?? []

  const header = (
    <PageHeader
      title="Overview"
      description="Your plan, data, and traffic at a glance."
    />
  )

  // Gate on an active subscription. Until the plan status loads, show skeleton
  // tiles matching the final layout so the page never flashes blank (or the
  // picker at an entitled user).
  if (planQuery.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <StatRowSkeleton />
      </div>
    )
  }

  if (!entitled) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <SubscribeGate
          plans={plan?.plans ?? []}
          orgId={orgId ?? undefined}
          lapsed={plan?.hasSubscription ?? false}
          trialEnded={plan?.trialEnded ?? false}
          canManageBilling={plan?.canManageBilling ?? false}
          accountManaged={plan?.accountManaged ?? false}
        />
      </div>
    )
  }

  const remainingBytes =
    balanceQuery.data?.remainingBytes ?? plan?.remainingBytes ?? 0

  // An org with credentials gets the working overview (top consumers + recent
  // activity); a fresh org gets the quickstart instead. Wait for the list to
  // resolve so the quickstart never flashes for an org that has credentials.
  const credentialsReady = !credentialsQuery.isPending
  const hasCredentials = credentials.length > 0

  return (
    <div className="flex flex-col gap-6">
      {header}

      <QuickActions />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DataRemainingCard
          pending={balanceQuery.isPending}
          remainingBytes={remainingBytes}
          plan={plan}
          usagePending={usageQuery.isPending}
          usage={usage}
          now={now}
        />
        <UsedThisPeriodCard pending={planQuery.isPending} plan={plan} />
        <CredentialsCard
          pending={credentialsQuery.isPending && !!orgId}
          credentials={credentials}
        />
        <PlanCard pending={planQuery.isPending} plan={plan} />
      </div>

      {hasCredentials ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Same card the usage page uses, at a fixed 30d window — one
              question, one component, one data source. */}
          <UsageByCredentialCard
            orgId={orgId}
            range="30d"
            description="Which credentials drove traffic in the last 30 days."
            topN={5}
          />
          <RecentActivityCard orgId={orgId} />
        </div>
      ) : credentialsReady ? (
        <QuickstartCard conn={connQuery.data} />
      ) : null}

      <UsageChartCard
        title="Data usage"
        description="Bandwidth consumed across all of this organization's credentials."
        queryKey={(range) => usageKeys.orgUsage(orgId, range)}
        fetcher={(range) => getOrgUsage({ data: { range } })}
        defaultRange="30d"
        height={240}
      />
    </div>
  )
}
